import {useRef} from 'react';
import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useTestObservations} from '../hooks/useTestObservations.ts';
import {useTestObservationsSearch} from '../hooks/useTestObservationsSearch.ts';
import {useTestObservationsSelection} from '../hooks/useTestObservationsSelection.ts';
import {useTestObservationsTableColumns} from '../hooks/useTestObservationsTableColumns.tsx';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {TriageSummary} from '../components/TriageSummary.tsx';
import {CascadeRow} from '../utils/cascadeRows.ts';
import config from '../config';

export const RecordsTable = () => {

    const {columns} = useTestObservationsTableColumns();
    const navigate = useNavigate();

    const {allowedActions} = useVerifiedPermissions();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's "N of M loaded" claim is measured against.
        items,
        rows,
        summary,
        loading,
        isError,
        nextToken,
        fetchRecords,
    } = useTestObservations();

    // Same module-scoped store the list hook filters on (FNP-815): this component
    // owns the input, useTestObservations owns the filtering, and they must share
    // one keyword. Never swap this for a local useState.
    const {
        keyword,
        handleFilterByKeyword,
    } = useTestObservationsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useTestObservationsSelection();

    // ⚠️ The table record is a CascadeRow WRAPPER, so selection keys off
    // `row.record.id` — the observation id — not `row.id` (which does not exist).
    // Same reason the table sets idAccessor='record.id'.
    let selectedRecords: CascadeRow[] = [];
    if (allowedActions.includes(rbacActions.DeleteTestObservation)) {
        selectedRecords = rows.filter(row => selection[row.record.id] === true);
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TriageSummary summary={summary}/>

            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load observations'>
                    The testing API did not return observations. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable<CascadeRow>
                columns={columns}
                records={rows}
                fetching={loading}
                // The table record is a CascadeRow wrapper, so the row key is the
                // nested observation id — without this the default `id` accessor
                // finds nothing and React re-keys every row on each render.
                idAccessor={'record.id'}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    (items as CascadeRow[]).forEach(item => {
                        handleRowSelection(item.record.id, true);
                    });
                }}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // A dependent row is muted via its cells rather than the row
                // background, so the indent stays the primary grouping cue.
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent(record.record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={summary.total}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'observation'}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
